import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import KeywordBubbleChart, {
  KeywordBubbleChartRef,
} from "./components/KeywordBubbleChart";
import KeywordsTable from "./components/KeywordsTable";
import DownloadChartButton from "./components/DownloadChartButton";
import ControlPanel from "./components/ControlPanel";
import AddAnchorKeywordModal from "./components/AddAnchorKeywordModal";
import AddKeywordsModal from "./components/AddKeywordsModal";
import VariantSelectionModal from "./components/VariantSelectionModal";
import ConfirmationModal from "./components/ConfirmationModal";
import DeleteKeywordsModal from "./components/DeleteKeywordsModal";
import Header, { HeaderRef } from "./components/Header";
import LoginPage from "./components/LoginPage";
import SetPasswordPage from "./components/SetPasswordPage";
import SignUpPage from "./components/SignUpPage";
import SignUpSuccessPage from "./components/SignUpSuccessPage";
import PricingPage from "./components/PricingPage";
import ContactPage from "./components/ContactPage";
import ForgotPasswordPage from "./components/ForgotPasswordPage";
import ResetPasswordPage from "./components/ResetPasswordPage";
import VerifyEmailPage from "./components/VerifyEmailPage";
import AdminPanel from "./components/admin/AdminPanel";
import EmailVerificationBanner from "./components/EmailVerificationBanner";
import GlobalBillingBanner from "./components/GlobalBillingBanner";
import AccountDeactivatedPage from "./components/AccountDeactivatedPage";
import EndSessionButton from "./components/EndSessionButton";
import KeywordTrendsAnimationPage from "./components/KeywordTrendsAnimationPage";
import AppLoader from "./components/AppLoader";
import { useAuth } from "./contexts/AuthContext";
import { ClientProvider, useClient } from "./contexts/ClientContext";
import { useFirstClient } from "./hooks/useFirstClient";
import { ClusterCenter, Domain, KeywordWithMetadata } from "./types";
import { isCenterPlottable } from "./utils/clusterCenter";
import {
  CreateClusterResponse,
  clusterApi,
  AnchorKeywordData,
  ClusterDataResponse,
} from "./services/api";
import "./App.css";
import { useToast } from "./components/Toast";
import { createTheme, ThemeProvider } from "@mui/material";
import OptionsBtn from "./components/OptionsBtn";
import { ClusterOption } from "./components/TypeaheadSelect";
import { getNextWeeklyRefreshDisplay } from "./utils/next-kws-refresh-display";
import { formatLastRefresh } from "./utils/formatLastRefresh";
import { parseServerDate } from "./utils/formatters";
import AddDomainBanner from "./components/AddDomainBanner";
import { LOW_REPLACEMENT_QUOTA_THRESHOLD } from "./config/constants";
import Footer from "./components/Footer";
import MobileBanner from "./components/MobileBanner";
import { MyLocalStorage } from "./utils/storage";
import CloseIcon from "@mui/icons-material/Close";
import useStorageState from "./hooks/useStorageState";

// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <AppLoader message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Smart redirect component that navigates to the first available domain
const SmartClientRedirect: React.FC<{
  buildPath: (clientId: string) => string;
}> = ({ buildPath }) => {
  const { firstClientId, isLoading } = useFirstClient();

  if (isLoading) {
    return <AppLoader message="Loading..." />;
  }

  return <Navigate to={buildPath(firstClientId)} replace />;
};

// Admin route wrapper (tenant admin or superadmin)
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { firstClientId } = useFirstClient();

  if (isLoading) {
    return <AppLoader message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user || (user.role !== "admin" && !user.is_superadmin)) {
    return <Navigate to={`/${firstClientId}`} replace />;
  }

  return <>{children}</>;
};

function AppContent(): React.JSX.Element {
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const { currentClient } = useClient();
  const navigate = useNavigate();
  const headerRef = useRef<HeaderRef>(null);
  const canDelete = !!user && (user.is_superadmin || user.role === "admin");
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);

  // Check if currently impersonating
  useEffect(() => {
    const checkImpersonation = () => {
      const adminToken = MyLocalStorage.jwt_token_admin.get();
      setIsImpersonating(!!adminToken);
    };

    checkImpersonation();
    // Check periodically in case it changes
    const interval = setInterval(checkImpersonation, 1000);
    return () => clearInterval(interval);
  }, []);

  // State for API data
  const [anchorKeywordsData, setAnchorKeywordsData] = useState<
    AnchorKeywordData[]
  >([]);
  const [clusterData, setClusterData] = useState<ClusterDataResponse | null>(
    null,
  );
  const [isLoadingAnchorKeywords, setIsLoadingAnchorKeywords] =
    useState<boolean>(true);
  const [isLoadingClusterData, setIsLoadingClusterData] =
    useState<boolean>(false);
  const [apiError, setApiError] = useState<string>("");
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const bumpActivity = useCallback(
    () => setActivityRefreshKey((k) => k + 1),
    [],
  );
  const { showToast } = useToast();

  // Ref for accessing chart instance for download
  const chartRef = useRef<KeywordBubbleChartRef>(null);

  // State for available domains
  const [availableDomains, setAvailableDomains] = useState<Domain[]>([]);
  const [isLoadingDomains, setIsLoadingDomains] = useState<boolean>(true);

  // State for last global refresh
  const [lastGlobalRefresh, setLastGlobalRefresh] = useState<string | null>(
    null,
  );

  // State for storage of ClosedZeroMetricBannerOnClusters
  const [
    closedZeroMetricBannerOnClusters,
    setClosedZeroMetricBannerOnClusters,
  ] = useStorageState("ClosedZeroMetricBannerOnClusters");

  // Check if the current domain is valid (exists in the tenant's domains)
  const hasValidDomain = useMemo(() => {
    if (availableDomains.length === 0) return false;
    if (availableDomains.every((d) => !d.domain)) return false;
    return availableDomains.some((d) => d.id === currentClient);
  }, [availableDomains, currentClient]);

  // Fetch available domains
  const fetchDomains = useCallback(async () => {
    if (isAuthLoading || !isAuthenticated) {
      setAvailableDomains([]);
      setIsLoadingDomains(false);
      return;
    }
    try {
      setIsLoadingDomains(true);
      const domains = await clusterApi.getClients();
      setAvailableDomains(domains);
    } catch (error) {
      console.error("Failed to fetch domains:", error);
      setAvailableDomains([]);
    } finally {
      setIsLoadingDomains(false);
    }
  }, [isAuthenticated, isAuthLoading]);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  // Fetch last global refresh time
  useEffect(() => {
    const fetchLastRefresh = async () => {
      if (isAuthLoading || !isAuthenticated) {
        return;
      }
      try {
        const response = await clusterApi.getLastGlobalRefresh();
        setLastGlobalRefresh(response.last_refresh_at);
      } catch (error) {
        console.error("Failed to fetch last global refresh:", error);
        setLastGlobalRefresh(null);
      }
    };

    fetchLastRefresh();
  }, [isAuthenticated, isAuthLoading]);

  // Build ClusterOption list (value = composite key) for the 3-column dropdown.
  const clusterOptions = useMemo((): ClusterOption[] => {
    return [...anchorKeywordsData]
      .sort((a, b) =>
        a.anchor_keyword
          .toLowerCase()
          .localeCompare(b.anchor_keyword.toLowerCase()),
      )
      .map((d) => ({
        value: `${d.anchor_keyword}||${d.language_code}||${d.location_code}`,
        keyword: d.anchor_keyword,
        languageCode: d.language_code,
        countryIsoCode: d.country_iso_code,
      }));
  }, [anchorKeywordsData]);

  // Flat string list kept for places that still need keyword-only strings.
  const anchorKeywords = useMemo(
    () => clusterOptions.map((o) => o.keyword),
    [clusterOptions],
  );

  // Fetch anchor keywords from API
  useEffect(() => {
    const fetchAnchorKeywords = async () => {
      if (isAuthLoading) {
        return;
      }
      if (!isAuthenticated) {
        setAnchorKeywordsData([]);
        setIsLoadingAnchorKeywords(false);
        return;
      }
      try {
        setIsLoadingAnchorKeywords(true);
        setApiError("");
        const data = await clusterApi.getAnchorKeywords(currentClient);
        setAnchorKeywordsData(data);
      } catch (error) {
        console.error("Failed to fetch anchor keywords:", error);
        setApiError("Failed to load anchor keywords.");
      } finally {
        setIsLoadingAnchorKeywords(false);
      }
    };

    fetchAnchorKeywords();
  }, [currentClient, isAuthenticated, isAuthLoading]);

  // Set the first anchor keyword as default when data loads
  const [selectedAnchor, setSelectedAnchor] = useState<string>("");

  // Full metadata for the currently selected cluster (looked up by composite key).
  const selectedAnchorData = useMemo(() => {
    const [kw, lang, loc] = selectedAnchor.split("||");
    return anchorKeywordsData.find(
      (d) =>
        d.anchor_keyword === kw &&
        d.language_code === lang &&
        String(d.location_code) === loc,
    );
  }, [anchorKeywordsData, selectedAnchor]);

  // Plain keyword string derived from the composite key selection.
  const selectedAnchorKeyword =
    selectedAnchorData?.anchor_keyword ?? selectedAnchor.split("||")[0] ?? "";

  // Get the creation date of the currently selected cluster
  const selectedClusterCreatedAt = selectedAnchorData?.created_at;

  // Update selected anchor when anchor keywords data changes
  useEffect(() => {
    if (clusterOptions.length > 0 && !selectedAnchor) {
      setSelectedAnchor(clusterOptions[0].value);
    }
  }, [clusterOptions, selectedAnchor]);

  // Fetch cluster data when selected anchor changes
  useEffect(() => {
    let trendRefetchTimer: ReturnType<typeof setTimeout> | null = null;

    const fetchClusterData = async (isRefetch = false) => {
      if (isAuthLoading || !isAuthenticated) {
        setClusterData(null);
        return;
      }
      if (!selectedAnchorKeyword) return;

      if (!isRefetch) {
        setClusterData(null);
      }

      try {
        if (!isRefetch) setIsLoadingClusterData(true);
        const data = await clusterApi.getClusterData(currentClient, {
          anchor_keyword: selectedAnchorKeyword,
          location_code: selectedAnchorData?.location_code ?? 2840,
          language_code: selectedAnchorData?.language_code ?? "en",
        });
        if (data.visualization_data && data.visualization_data.length === 0) {
          showToast("error", "No visualization available!", 10000);
        }

        setClusterData(data);

        if (!isRefetch && data.visualization_data?.length > 0) {
          const hasTrends = data.visualization_data.some(
            (item) =>
              item.trend &&
              (item.trend.week != null ||
                item.trend.month != null ||
                item.trend.life != null),
          );
          if (!hasTrends) {
            trendRefetchTimer = setTimeout(() => fetchClusterData(true), 15000);
          }
        }
      } catch (error) {
        if (!isRefetch) console.error("Failed to fetch cluster data:", error);
      } finally {
        if (!isRefetch) setIsLoadingClusterData(false);
      }
    };

    fetchClusterData();

    return () => {
      if (trendRefetchTimer) clearTimeout(trendRefetchTimer);
    };
  }, [selectedAnchor, currentClient, isAuthenticated, isAuthLoading]);
  const [useDynamicAxes, setUseDynamicAxes] = useState<boolean>(true);
  const [showLabels, setShowLabels] = useState<boolean>(false);
  const [colorMode, setColorMode] = useState<"trend" | "similarity">("trend");
  const [trendPeriod, setTrendPeriod] = useState<"week" | "month" | "life">(
    "life",
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isVariantModalOpen, setIsVariantModalOpen] = useState<boolean>(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [clusterDeleteMessage, setClusterDeleteMessage] = useState<string>("");
  const [isDeleteKeywordsModalOpen, setIsDeleteKeywordsModalOpen] =
    useState<boolean>(false);
  const [isAddKeywordsModalOpen, setIsAddKeywordsModalOpen] =
    useState<boolean>(false);
  const [clusterResponse, setClusterResponse] =
    useState<CreateClusterResponse | null>(null);

  // Handle successful cluster creation
  const handleClusterCreated = async (response: CreateClusterResponse) => {
    setClusterResponse(response);
    setIsVariantModalOpen(true);
  };

  // Handle successful variant processing
  const handleVariantsProcessed = async () => {
    // Refresh the anchor keywords list
    try {
      showToast("success", "Cluster created successfully!", 10000);
      const data = await clusterApi.getAnchorKeywords(currentClient);
      setAnchorKeywordsData(data);

      // Automatically select the newly created cluster.
      if (clusterResponse && clusterResponse.anchor_keyword) {
        setSelectedAnchor(
          `${clusterResponse.anchor_keyword}||${clusterResponse.language_code ?? "en"}||${clusterResponse.location_code ?? 2840}`,
        );
      }

      // Refresh tracked keywords count
      headerRef.current?.refreshTrackedKeywordsCount();
      bumpActivity();

      // Warning toast when monthly cluster-replacement budget is running low.
      try {
        const usage = await clusterApi.getMyUsage();
        const limit = usage.cluster_replacements_limit;
        const used = usage.cluster_replacements_used ?? 0;
        if (typeof limit === "number") {
          const remaining = Math.max(0, limit - used);
          if (remaining <= 3) {
            showToast(
              "warning",
              remaining > 0
                ? `Only ${remaining} cluster replacements left this billing period.`
                : `You won’t be able to create cluster again at the current billing period since you’ve reached the limit.`,
              10000,
            );
          }
        }
      } catch (e) {
        // Non-fatal: the cluster was already created.
        console.warn("Failed to fetch tenant usage for warning toast:", e);
      }
    } catch (error) {
      console.error("Failed to refresh anchor keywords:", error);
    }
  };

  const handleAddAndDeleteKeyword = async () => {
    if (selectedAnchorKeyword) {
      const fetchClusterData = async () => {
        try {
          setIsLoadingClusterData(true);
          const data = await clusterApi.getClusterData(currentClient, {
            anchor_keyword: selectedAnchorKeyword,
            location_code: selectedAnchorData?.location_code ?? 2840,
            language_code: selectedAnchorData?.language_code ?? "en",
          });
          setClusterData(data);

          // Warning toast when keyword-replacement budget is running low.
          const usage = data.usage;
          if (usage && usage.limit !== null) {
            const remaining = Math.max(0, usage.limit - usage.used);
            if (remaining <= 3) {
              showToast(
                "warning",
                remaining > 0
                  ? `Only ${remaining} keyword replacements left for this cluster this billing period.`
                  : `You won’t be able to add keywods to this cluster at the current billing period since you’ve done too much replacements! Limit: ${usage.limit}`,
                10000,
              );
            }
          }

          // Refresh tracked keywords count
          headerRef.current?.refreshTrackedKeywordsCount();
        } catch (error) {
          console.error("Failed to refresh cluster data:", error);
        } finally {
          setIsLoadingClusterData(false);
        }
      };
      fetchClusterData();
    }
  };

  // Refresh anchor keyword
  const handleRefreshAnchorKeyword = async () => {
    if (!selectedAnchorKeyword) return;

    setIsLoadingClusterData(true);
    try {
      const response = await clusterApi.refreshCluster(currentClient, {
        anchor_keyword: selectedAnchorKeyword,
        location_code: selectedAnchorData?.location_code ?? 2840,
        language_code: selectedAnchorData?.language_code ?? "en",
      });
      setClusterData(response);
      // Refresh tracked keywords count
      headerRef.current?.refreshTrackedKeywordsCount();
      bumpActivity();

      if (
        response &&
        Array.isArray(response.visualization_data) &&
        response.visualization_data.length === 0
      ) {
        showToast("error", "No visualization available!", 10000);
      }
    } catch (error) {
      console.error("Failed to refresh anchor keyword:", error);
    } finally {
      setIsLoadingClusterData(false);
    }
  };

  // Delete anchor keyword
  const handleDeleteAnchorKeyword = async () => {
    if (!selectedAnchorKeyword) return;

    const baseMessage = `Are you sure you want to delete "${selectedAnchorKeyword}" and all its associated data? This action cannot be undone.`;

    try {
      const usage = await clusterApi.getMyUsage();
      const limit = usage.cluster_replacements_limit ?? null;
      const used = usage.cluster_replacements_used ?? 0;
      const remaining = limit !== null ? Math.max(0, limit - used) : null;

      if (remaining !== null && remaining <= LOW_REPLACEMENT_QUOTA_THRESHOLD) {
        setClusterDeleteMessage(
          `${baseMessage} You currently have ${remaining} cluster replacement(s) remaining this billing period. Once exhausted, you will not be able to create new clusters until your billing period resets.`,
        );
      } else {
        setClusterDeleteMessage(baseMessage);
      }
    } catch {
      setClusterDeleteMessage(baseMessage);
    }

    setIsConfirmationModalOpen(true);
  };

  // Confirm delete anchor keyword
  const confirmDeleteAnchorKeyword = async () => {
    if (!selectedAnchorKeyword) return;

    setIsLoadingClusterData(true);
    try {
      await clusterApi.deleteAnchorKeyword(
        currentClient,
        selectedAnchorKeyword,
        selectedAnchorData?.language_code ?? "en",
        selectedAnchorData?.location_code ?? 2840,
      );
      const data = await clusterApi.getAnchorKeywords(currentClient);
      setAnchorKeywordsData(data);
      // Reset selected anchor to the first remaining cluster.
      const remaining = [...data].sort((a, b) =>
        a.anchor_keyword
          .toLowerCase()
          .localeCompare(b.anchor_keyword.toLowerCase()),
      );

      if (remaining.length > 0) {
        setSelectedAnchor(
          `${remaining[0].anchor_keyword}||${remaining[0].language_code ?? "en"}||${remaining[0].location_code ?? 2840}`,
        );
      } else {
        setSelectedAnchor("");
        setClusterData(null);
      }
      bumpActivity();
    } catch (error) {
      console.error("Failed to delete anchor keyword:", error);
    } finally {
      setIsLoadingClusterData(false);
    }
  };

  const handleCloseZeroMetricBanner = useCallback(() => {
    if (selectedAnchorData) {
      const newData =
        MyLocalStorage.ClosedZeroMetricBannerOnClusters.get() ?? [];
      newData.push(selectedAnchorData.cluster_id);

      setClosedZeroMetricBannerOnClusters(newData);
    }
  }, [selectedAnchorData]);

  // Filter keywords based on selected anchor using API data
  const filteredData = useMemo(() => {
    if (!clusterData || !clusterData.visualization_data) return [];

    // Convert API data to the format expected by the chart
    const result: KeywordWithMetadata[] = clusterData.visualization_data.map(
      (item, index) => ({
        keyword: item.keyword,
        volume: item.volume,
        difficulty: item.difficulty,
        ranking: item.ranking,
        trend: item.trend,
        isAnchor: item.keyword === selectedAnchorKeyword,
        parentAnchor: clusterData.anchor_keyword,
        relevance_score: item.relevance_score,
        semantic_similarity: item.semantic_similarity,
        length_score: item.length_score,
        intent_score: item.intent_score,
        keyword_overlap: item.keyword_overlap,
        openai_similarity: item.openai_similarity,
        ranking_url: item.ranking_url,
        rank_is_stale: item.rank_is_stale ?? false,
      }),
    );

    return result;
  }, [clusterData]);

  // Map the API's computed cluster center onto the chart's domain shape.
  // Null when the backend returned no center (e.g. an empty cluster).
  const clusterCenter = useMemo<ClusterCenter | null>(() => {
    const c = clusterData?.center;
    if (!c) return null;
    return {
      keyword: c.keyword,
      volume: c.volume,
      difficulty: c.difficulty,
      ranking: c.ranking,
      trend: c.trend,
      isCenter: true,
    };
  }, [clusterData]);

  // this state will be set in Header to be shared to AddKeywordsModal
  const [maxKeywordsPerClusterLimit, setMaxKeywordsPerClusterLimit] = useState<
    undefined | number | "inf"
  >(undefined);

  // delete cluster ids on ClosedZeroMetricBannerOnClusters localstorage which cluster.createdAt is known to be earlier than 24h ago
  // so as to not keep unnecessary data and burden the storage
  useEffect(() => {
    const storageData = MyLocalStorage.ClosedZeroMetricBannerOnClusters.get();
    if (!storageData) return;

    // filter ids which are known to be created earlier than 24h ago
    const clusterIdsToKeep = storageData.filter((clusterId) => {
      const clusterData = anchorKeywordsData.find(
        (d) => d.cluster_id === clusterId,
      );

      if (!clusterData) return true;
      if (clusterData?.created_at) {
        const createdAt = parseServerDate(clusterData.created_at).getTime();
        const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
        const earlierThan24hAgo = createdAt < twentyFourHoursAgo;
        if (earlierThan24hAgo) return false;
        else return true;
      }
      return true;
    });

    setClosedZeroMetricBannerOnClusters(clusterIdsToKeep);
  }, [anchorKeywordsData]);

  return (
    <div className="App">
      {isImpersonating && <EndSessionButton />}
      {/* Show app loader until anchor keywords are loaded */}
      {isLoadingAnchorKeywords ? (
        <AppLoader message="Loading anchor keywords..." />
      ) : (
        <>
          <MobileBanner />
          <Header
            ref={headerRef}
            setMaxKeywordsPerClusterLimit={setMaxKeywordsPerClusterLimit}
            onClickAddAnchorKeyword={() => setIsModalOpen(true)}
            hasValidDomain={hasValidDomain}
            onDomainUpdated={fetchDomains}
          />
          <EmailVerificationBanner />
          <AddDomainBanner domains={availableDomains} />

          <main className="App-main">
            <div className="dashboard-layout">
              <div className="sidebar">
                <ControlPanel
                  anchorKeywords={clusterOptions}
                  selectedAnchor={selectedAnchor}
                  setSelectedAnchor={setSelectedAnchor}
                  useDynamicAxes={useDynamicAxes}
                  setUseDynamicAxes={setUseDynamicAxes}
                  showLabels={showLabels}
                  setShowLabels={setShowLabels}
                  colorMode={colorMode}
                  setColorMode={setColorMode}
                  trendPeriod={trendPeriod}
                  setTrendPeriod={setTrendPeriod}
                  keywordData={filteredData}
                />

                <div className="w-full bg-white rounded-xl py-4 px-5 grid grid-cols-[1fr_auto_1fr] items-center gap-6">
                  <div className="text-center">
                    <div className="font-semibold text-xs text-neutral-400 leading-none mb-2">
                      Cluster Location
                    </div>
                    <div className="font-semibold text-neutral-800 leading-none">
                      {selectedAnchorData?.location_name}
                    </div>
                  </div>
                  <div className="h-full w-0.5 bg-neutral-100" />
                  <div className="text-center">
                    <div className="font-semibold text-xs text-neutral-400 leading-none mb-2">
                      Cluster Language
                    </div>
                    <div className="font-semibold text-neutral-800 leading-none">
                      {selectedAnchorData?.language_name}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h1
                    className="font-bold text-4xl"
                    style={{
                      fontFamily: "Roundo",
                    }}
                  >
                    Keyword Cluster
                  </h1>

                  <div className="flex items-center gap-2">
                    <DownloadChartButton
                      chartRef={chartRef}
                      showCenterMarker={isCenterPlottable(clusterCenter)}
                      anchorKeyword={selectedAnchorKeyword}
                      languageCode={selectedAnchorData?.language_code ?? "en"}
                      locationCode={selectedAnchorData?.location_code ?? 2840}
                      fetchDate={
                        clusterData?.data_fetched_at ?? selectedClusterCreatedAt
                      }
                      disabled={isLoadingClusterData || !clusterData}
                      domainName={
                        availableDomains.find((d) => d.id === currentClient)
                          ?.domain ?? ""
                      }
                      clusterSize={clusterData?.cluster_size ?? 0}
                      totalVolume={
                        clusterData?.summary?.total_search_volume ?? 0
                      }
                      avgDifficulty={
                        clusterData?.summary?.avg_difficulty ?? 0
                      }
                      lastRefreshed={formatLastRefresh(
                        clusterData?.data_fetched_at ?? lastGlobalRefresh
                      )}
                      nextUpdate={getNextWeeklyRefreshDisplay("monday", 3)}
                    />

                    <button
                      className="rounded-full bg-[#e7e2f8] py-[14px] px-[18px] flex items-center gap-1.5"
                      onClick={() => {
                        navigate(`/${currentClient}/keywords-animation`, {
                          state: {
                            selectedAnchor: selectedAnchorKeyword,
                            initialData: filteredData,
                            anchorKeywords,
                            // Pass the cluster's creation date so the animation page can disable date-picker dates before the cluster existed.
                            clusterCreatedAt: selectedClusterCreatedAt,
                            locationCode:
                              selectedAnchorData?.location_code ?? 2840,
                            languageCode:
                              selectedAnchorData?.language_code ?? "en",
                          },
                        });
                      }}
                    >
                      <img src="/icons/sparkles.svg" />
                      <span className="font-semibold leading-[1] text-my-purple">
                        Animation
                      </span>
                    </button>

                    <OptionsBtn
                      isLoadingClusterData={isLoadingClusterData}
                      addKeywordsHandler={() => {
                        setIsAddKeywordsModalOpen(true);
                      }}
                      removeKeywordsHandler={() => {
                        setIsDeleteKeywordsModalOpen(true);
                      }}
                      deleteClusterHandler={() => {
                        handleDeleteAnchorKeyword();
                      }}
                      canDelete={canDelete}
                    />
                  </div>
                </div>

                <div className="flex gap-2.5">
                  <div className="grid grid-cols-3 gap-x-2.5">
                    <div className="px-4 py-3.5 bg-white rounded-xl">
                      <div className="font-semibold text-sm text-neutral-400 leading-[1] mb-2">
                        Keywords
                      </div>
                      <div className="font-semibold text-2xl leading-[1]">
                        {clusterData?.cluster_size}
                      </div>
                    </div>
                    <div className="px-4 py-3.5 bg-white rounded-xl">
                      <div className="font-semibold text-sm text-neutral-400 leading-[1] mb-2">
                        Total Volume
                      </div>
                      <div className="font-semibold text-2xl leading-[1]">
                        {clusterData?.summary.total_search_volume}
                      </div>
                    </div>
                    <div className="px-4 py-3.5 bg-white rounded-xl">
                      <div className="font-semibold text-sm text-neutral-400 leading-[1] mb-2">
                        Average Difficulty
                      </div>
                      <div className="font-semibold text-2xl leading-[1]">
                        {clusterData?.summary?.avg_difficulty != null
                          ? Math.round(clusterData.summary.avg_difficulty * 100) / 100
                          : "-"}
                      </div>
                    </div>
                  </div>

                  <div className="px-4 py-3.5 bg-white rounded-xl flex gap-5 flex-1">
                    <div className="flex-1 flex flex-col justify-between">
                      <div className="font-semibold text-sm text-neutral-400 leading-[1] mb-2">
                        Last refreshed
                      </div>
                      <div className="font-medium leading-[1]">
                        {formatLastRefresh(
                          clusterData?.data_fetched_at ?? lastGlobalRefresh,
                        )}
                      </div>
                    </div>
                    <div className="w-[1px] h-full bg-neutral-300" />
                    <div className="flex-1 flex flex-col justify-between">
                      <div className="font-semibold text-sm text-neutral-400 leading-[1] mb-2">
                        Next update
                      </div>
                      <div className="font-medium leading-[1]">
                        {getNextWeeklyRefreshDisplay("monday", 3)}
                      </div>
                    </div>
                  </div>
                </div>

                {filteredData.filter(
                  (kw) =>
                    kw.volume === 0 && kw.difficulty === 0 && kw.ranking === 0,
                ).length >= 4 &&
                  // banner shouldn't be shown 24h after cluster creation
                  selectedClusterCreatedAt &&
                  Date.now() -
                    parseServerDate(selectedClusterCreatedAt).getTime() <=
                    24 * 60 * 60 * 1000 &&
                  // banner shouldn't be shown if user have clicked to close it
                  !(closedZeroMetricBannerOnClusters ?? []).includes(
                    selectedAnchorData.cluster_id,
                  ) && (
                    <div className="bg-[#fff1ec] rounded-xl py-2 px-4 relative">
                      <CloseIcon
                        sx={{ fontSize: 16, color: "#acacac" }}
                        className="cursor-pointer hover:text-my-orange absolute right-3 top-2"
                        onClick={handleCloseZeroMetricBanner}
                      />
                      <div className="text-sm text-my-orange font-medium text-center">
                        Some of your keywords have{" "}
                        <span className="font-bold underline underline-offset-2">
                          zero volume and difficulty
                        </span>
                        . Consider replacing them! <br />
                        For that, click on Options and remove the ones with no
                        volume or difficulty, then add new ones.
                      </div>
                    </div>
                  )}

                <div className="chart-container">
                  {isLoadingClusterData ? (
                    <div className="loading">Loading cluster data...</div>
                  ) : (
                    <KeywordBubbleChart
                      ref={chartRef}
                      data={filteredData}
                      center={clusterCenter}
                      useDynamicAxes={useDynamicAxes}
                      showLabels={showLabels}
                      colorMode={colorMode}
                      trendPeriod={trendPeriod}
                    />
                  )}
                </div>

                <KeywordsTable
                  keywords={filteredData}
                  center={clusterCenter}
                  selectedAnchor={selectedAnchorKeyword}
                  clientId={currentClient}
                  isLoading={isLoadingClusterData}
                  locationCode={selectedAnchorData?.location_code ?? 2840}
                  languageCode={selectedAnchorData?.language_code ?? "en"}
                />
              </div>
            </div>
          </main>

          {/* Modal for Adding Anchor Keyword */}
          <AddAnchorKeywordModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onClusterCreated={handleClusterCreated}
            clusterOptions={clusterOptions}
          />

          {/* Modal for Selecting Variants */}
          {clusterResponse && (
            <VariantSelectionModal
              isOpen={isVariantModalOpen}
              onClose={() => setIsVariantModalOpen(false)}
              anchorKeyword={clusterResponse.anchor_keyword}
              variants={clusterResponse.variants}
              onSuccess={handleVariantsProcessed}
              locationCode={clusterResponse.location_code}
              languageCode={clusterResponse.language_code}
            />
          )}

          {/* Confirmation Modal for Deleting Anchor Keyword */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            onClose={() => setIsConfirmationModalOpen(false)}
            onConfirm={confirmDeleteAnchorKeyword}
            title="Delete Cluster"
            message={clusterDeleteMessage}
            confirmText="Delete"
            cancelText="Cancel"
            variant="danger"
          />

          {/* Modal for Deleting Keywords */}
          <DeleteKeywordsModal
            isOpen={isDeleteKeywordsModalOpen}
            onClose={() => setIsDeleteKeywordsModalOpen(false)}
            keywords={filteredData}
            anchorKeyword={selectedAnchorKeyword}
            onDeleteSuccess={handleAddAndDeleteKeyword}
            locationCode={selectedAnchorData?.location_code ?? 2840}
            languageCode={selectedAnchorData?.language_code ?? "en"}
            replacementUsage={clusterData?.usage}
          />

          {/* Modal for Adding Keywords */}
          <AddKeywordsModal
            isOpen={isAddKeywordsModalOpen}
            onClose={() => setIsAddKeywordsModalOpen(false)}
            anchorKeyword={selectedAnchorKeyword}
            currentClusterSize={clusterData?.cluster_size || 0}
            maxKeywordsPerClusterLimit={maxKeywordsPerClusterLimit}
            replacementUsage={clusterData?.usage}
            onSuccess={handleAddAndDeleteKeyword}
            locationCode={selectedAnchorData?.location_code ?? 2840}
            languageCode={selectedAnchorData?.language_code ?? "en"}
          />
          <Footer />
        </>
      )}
    </div>
  );
}

const theme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: "Google Sans Flex",
          fontSize: "12px",
          fontWeight: "600",
          color: "#FFF",
          background: "#000000c7",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: "Google Sans Flex",
          backgroundColor: "#FFF",
          "&:hover": {
            backgroundColor: "#f6edff",
          },
          "&.Mui-selected": {
            backgroundColor: "#e7d2ff !important",
            "&:hover": {
              backgroundColor: "#e5ceff",
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          border: "1px solid #ebd6ff",
          borderRadius: "12px",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: "50px",
          background: "#FFF",
          fontFamily: "Google Sans Flex",
          fontWeight: 500,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "transparent",
          },
          "& .MuiSelect-select": {
            padding: "12px 20px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "transparent",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#8642C7",
          },
        },
      },
    },
  },
});

function App(): React.JSX.Element {
  return (
    <ThemeProvider theme={theme}>
      {/* Rendered above the route tree so a single instance is shared by every protected page. The component decides internally whether to show, hide, or skip on the billing/deactivated routes. */}
      <GlobalBillingBanner />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/invite/accept" element={<SetPasswordPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/signup/success" element={<SignUpSuccessPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route
          path="/account-deactivated"
          element={<AccountDeactivatedPage />}
        />
        <Route
          path="/"
          element={
            <SmartClientRedirect buildPath={(clientId) => `/${clientId}`} />
          }
        />
        <Route
          path="/keywords-animation"
          element={
            <ProtectedRoute>
              <SmartClientRedirect
                buildPath={(clientId) => `/${clientId}/keywords-animation`}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <ClientProvider>
                <AdminPanel />
              </ClientProvider>
            </AdminRoute>
          }
        />
        <Route
          path="/:client"
          element={
            <ProtectedRoute>
              <ClientProvider>
                <AppContent />
              </ClientProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/:client/keywords-animation"
          element={
            <ProtectedRoute>
              <ClientProvider>
                <KeywordTrendsAnimationPage />
              </ClientProvider>
            </ProtectedRoute>
          }
        />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
